import React, { useState, useEffect, useMemo } from 'react';
import { Col, FormGroup, Input, Row, Label } from 'reactstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import Loading from '../../components/Loading/Loading';
import paginationFactory from 'react-bootstrap-table2-paginator';
import moment from 'moment';
import axios from 'axios';

import { STATUS_OPTIONS, TYPE_OPTIONS } from './constants';

const IndustryReports = () => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  // Table pagination and sorting state
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalSize, setTotalSize] = useState(0);
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  // Filter state
  const [nameFilter, setNameFilter] = useState('');
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Debounce logic for name filter
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedNameFilter(nameFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [nameFilter]);

  const createOptionFormatter = (options) => (cell) => {
    const option = options.find((opt) => opt.value === cell);
    return option ? option.label : cell;
  };

  const columns = useMemo(
    () => [
      {
        dataField: 'name',
        text: 'Name',
        sort: true,
      },
      {
        dataField: 'type',
        text: 'Type',
        sort: true,
        formatter: createOptionFormatter(TYPE_OPTIONS),
      },
      {
        dataField: 'status',
        text: 'Status',
        sort: true,
        formatter: createOptionFormatter(STATUS_OPTIONS),
      },
      {
        dataField: 'created',
        text: 'Created',
        sort: true,
        formatter: (cell) => (cell ? moment(cell).format('DD/MM/YYYY HH:mm:ss') : ''),
        sortValue: (cell) => (cell ? moment(cell).valueOf() : 0),
      },
      {
        dataField: 'updated',
        text: 'Updated',
        sort: true,
        formatter: (cell) => (cell ? moment(cell).format('DD/MM/YYYY HH:mm:ss') : ''),
        sortValue: (cell) => (cell ? moment(cell).valueOf() : 0),
      },
      {
        dataField: 'content',
        text: 'Content',
        formatter: (cell) => {
          try {
            return (
              <pre
                style={{
                  whiteSpace: 'pre',
                  overflowX: 'auto',
                  maxWidth: '500px',
                  maxHeight: '150px',
                  textAlign: 'left',
                  fontSize: '11px',
                  margin: 0,
                }}
              >
                {JSON.stringify(cell, null, 2)}
              </pre>
            );
          } catch (e) {
            return cell;
          }
        },
      },
    ],
    []
  );

  // Fetch reports data from API
  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.token;
      const params = {
        page: page - 1,
        size: size,
        sortBy: sortBy,
        sortDir: sortDir,
      };

      // Add filters only if they have values
      if (debouncedNameFilter) {
        params.name = debouncedNameFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (typeFilter) {
        params.type = typeFilter;
      }

      const response = await axios.get('/df/dataflows/rest/v1/reports', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTableData(response.data.content || []);
      setTotalSize(response.data.totalElements || 0);
    } catch (error) {
      setTableData([]);
      setTotalSize(0);
    }
    setLoading(false);
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchReports();
  }, [page, size, sortBy, sortDir, debouncedNameFilter, statusFilter, typeFilter]);

  // Table change handler
  const handleTableChange = (type, { page, sizePerPage, sortField, sortOrder }) => {
    if (type === 'sort') {
      setSortBy(sortField);
      setSortDir(sortOrder);
    }
    if (type === 'pagination') {
      setPage(page);
      setSize(sizePerPage);
    }
  };

  // Reset to first page when filters change
  const handleFilterChange = (filterSetter, value) => {
    filterSetter(value);
    setPage(1);
  };

  return (
    <div className='organization'>
      <Row className='subheader'>
        <Col className='col-8'>
          <h1>Industry Reports</h1>
        </Col>
      </Row>

      <div className='page-cnt'>
        <Row className='mb-3'>
          <Col md={4}>
            <FormGroup>
              <Label for='nameFilter'>Filter by Name</Label>
              <Input
                id='nameFilter'
                type='text'
                className='form-control'
                placeholder='Search by name...'
                value={nameFilter}
                onChange={(e) => handleFilterChange(setNameFilter, e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for='statusFilter'>Status</Label>
              <Input
                id='statusFilter'
                type='select'
                className='form-control'
                value={statusFilter}
                onChange={(e) => handleFilterChange(setStatusFilter, e.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label for='typeFilter'>Type</Label>
              <Input
                id='typeFilter'
                type='select'
                className='form-control'
                value={typeFilter}
                onChange={(e) => handleFilterChange(setTypeFilter, e.target.value)}
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col className='col-12'>
            {loading && <Loading />}
            <BootstrapTable
              classes='table-hover'
              wrapperClasses='table-responsive'
              noDataIndication={() => <div>No records in table.</div>}
              columns={columns}
              data={tableData}
              keyField={'id'}
              bordered={false}
              hover
              remote={{
                filter: false,
                pagination: true,
                sort: true,
              }}
              pagination={paginationFactory({
                page: page,
                sizePerPage: size,
                totalSize: totalSize,
                sizePerPageList: [
                  { text: '10', value: 10 },
                  { text: '20', value: 20 },
                  { text: '50', value: 50 },
                ],
              })}
              onTableChange={handleTableChange}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default IndustryReports;
